import type { ChatModelAdapter } from "@assistant-ui/react";
import { effect, Signal } from "@preact/signals-react";
import { log } from "../../library/logger";
import { playStreamingTextToSpeech } from "./textToSpeech";

export function createChatModelAdapter(
  value: Signal<unknown>,
  messagesSignal: Signal<unknown>,
  ttsStream: ReturnType<typeof playStreamingTextToSpeech>
): ChatModelAdapter {
  type TextPart = { type: "text"; text: string };

  // Complete NG... component block
  const NG_COMPONENT_REGEX = /<NG[A-Za-z0-9]*\b[\s\S]*?<\/NG[A-Za-z0-9]*>/g;

  return {
    async *run({ messages: conversationMessages, abortSignal }) {
      const tag = "NGAssistant";

      try {
        if (conversationMessages.length === 0) {
          throw new Error("No messages provided");
        }

        const formattedMessages = conversationMessages.map((msg) => ({
          role: msg.role,
          content: msg.content.map((c) => c.text).join("\n"),
        }));

        value.value = null;

        // Streaming state
        let streamEnded = false;
        let errorOccurred = false;
        let errorMessage = "";

        // Stable, frozen prefix parts (kept by reference across yields)
        const frozenParts: TextPart[] = [];
        // Mutable tail buffer (text not yet frozen)
        let tail = "";

        // For avoiding no-op yields
        let lastYieldedFrozenCount = 0;
        let lastYieldedTail = "";

        let effectCleanup: (() => void) | undefined;
        let currentResolver: ((parts: TextPart[]) => void) | undefined;

        // TTS streaming controller
        //let ttsStream: ReturnType<typeof playStreamingTextToSpeech> | null = null;
        //let ttsController: TextToSpeechController | null = null;
        let ttsChunkCount = 0;

        const createContentPromise = () =>
          new Promise<TextPart[]>((resolve) => {
            currentResolver = resolve;
          });

        // Move any complete blocks from tail into frozenParts, return true if something changed
        const flushCompletesIntoFrozen = (): boolean => {
          let changed = false;
          let idx = 0;
          const matches = Array.from(tail.matchAll(NG_COMPONENT_REGEX));
          if (matches.length) {
            for (const m of matches) {
              const start = m.index;
              // plain text before the component
              if (start > idx) {
                const pre = tail.slice(idx, start);
                if (pre) {
                  frozenParts.push({ type: "text", text: pre });
                  changed = true;
                }
              }

              const normalizedComponent = m[0].includes("<NGSuggestions")
                ? m[0].replace(/config='([^]*)'/, (_, rawConfig) => {
                    const normalizedConfig = rawConfig
                      .replace(/\\"/g, '"') // fix escaped double quotes
                      .replace(/\\\\/g, "\\") // collapse backslashes
                      .replace(/\\(['’])/g, "$1") // remove escapes before straight or curly apostrophes
                      .replace(/'/g, "’"); // convert any remaining straight apostrophes
                    return `config='${normalizedConfig}'`;
                  })
                : m[0];

              // the component block itself
              log.debug(tag, "got complete ng-component: ", m[0]);
              log.debug(tag, "  normalized ng-component: ", normalizedComponent);
              frozenParts.push({ type: "text", text: normalizedComponent });
              changed = true;

              idx = start + m[0].length;
            }
            // Keep only the remainder after the last complete component
            tail = tail.slice(idx);
          }

          // If tail contains a new partial start of a component, do not yield that partial.
          // Promote any preceding plain text to frozen so partial does not get rendered.
          const partialStartMatch = tail.match(/<NG([A-Za-z0-9]*)\b[^>]*$/);
          if (partialStartMatch) {
            const tagName = partialStartMatch[1];
            const partialStart = tail.lastIndexOf(`<NG${tagName}`);
            // Only treat as partial if there is no matching close tag after this open tag
            const closeTag = `</NG${tagName}>`;
            const afterOpen = tail.slice(partialStart);
            const hasUnclosed = !afterOpen.includes(closeTag);
            if (hasUnclosed) {
              const pre = tail.slice(0, partialStart);
              if (pre) {
                frozenParts.push({ type: "text", text: pre });
                changed = true;
              }
              // keep only the incomplete component fragment in tail (not yielded)
              tail = tail.slice(partialStart);
            }
          }

          return changed;
        };

        const buildPartsForYield = (): TextPart[] => {
          // Only include tail if it does not start with an incomplete component
          const incompleteMatch = tail.match(/^<NG([A-Za-z0-9]*)\b/);
          const tailStartsWithIncomplete = incompleteMatch && !tail.includes(`</NG${incompleteMatch[1]}>`);
          if (tail && !tailStartsWithIncomplete) {
            return [...frozenParts, { type: "text", text: tail }];
          }
          return [...frozenParts];
        };

        const createEffectIfNeeded = () => {
          if (effectCleanup) return;

          effectCleanup = effect(() => {
            const chunk = value.value;
            let hasAudio = false;

            // Error envelope from backend
            if (chunk && typeof chunk === "object" && "success" in chunk && !chunk.success && "reasons" in chunk) {
              const errorChunk = chunk as { success: boolean; reasons: Array<{ message: string }> };
              if (errorChunk.reasons && errorChunk.reasons.length > 0) {
                errorOccurred = true;
                errorMessage =
                  "<span class='error'>Sorry, I encountered an error while processing your request. Please try again.</span>";
                streamEnded = true;
                log.error(tag, "Backend error", errorChunk.reasons[0].message);

                if (currentResolver) {
                  currentResolver([{ type: "text", text: errorMessage }]);
                  currentResolver = undefined;
                }
                return;
              }
            }

            if (!chunk || typeof chunk !== "object") return;

            const typedChunk = chunk as { type: string; result?: { content: string } };

            if (typedChunk.type === "stream_end" || abortSignal.aborted) {
              // Final flush: move any remaining completes, then yield once
              streamEnded = true;
              if (hasAudio) {
                ttsStream.playFromChunks();
              }

              // Signal TTS that all chunks have been received
              if (ttsStream) {
                ttsStream.signalAllChunksReceived();
              }

              const parts = buildPartsForYield();

              if (currentResolver) {
                // If we never yielded tail because it was an incomplete component, it will not be included
                currentResolver(errorOccurred ? [{ type: "text", text: errorMessage }] : parts);
                currentResolver = undefined;
              }
            } else if (typedChunk.type === "content_chunk" && typedChunk.result?.content) {
              // Append new bytes to mutable tail
              tail += typedChunk.result.content;

              // Freeze any complete component blocks and any text before a partial component start
              const changed = flushCompletesIntoFrozen();

              // Yield if something changed in frozen, or tail changed since last yield
              const parts = buildPartsForYield();
              const tailForCompare = parts.length && parts[parts.length - 1] ? parts[parts.length - 1].text ?? "" : "";
              const frozenCountChanged = frozenParts.length !== lastYieldedFrozenCount;
              const tailChanged = tailForCompare !== lastYieldedTail;

              if (changed || frozenCountChanged || tailChanged) {
                lastYieldedFrozenCount = frozenParts.length;
                lastYieldedTail = tailForCompare;

                if (currentResolver) {
                  currentResolver(parts);
                  currentResolver = undefined;
                }
              }
            } else if (typedChunk.type === "audio_chunk" && typedChunk.result?.content) {
              // Convert base64 to Uint8Array
              hasAudio = true;
              const base64 = typedChunk.result.content;
              const binaryString = atob(base64);
              const len = binaryString.length;
              const bytes = new Uint8Array(len);
              for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }

              if (ttsStream) {
                ttsStream.appendChunk(bytes);
                ttsChunkCount++;

                if (ttsChunkCount >= 40) {
                  ttsStream.playFromChunks();
                }
              }
            }
          });
        };

        // Kick off backend by setting the messages signal
        messagesSignal.value = formattedMessages;

        // streaming loop
        while (!streamEnded && !errorOccurred && !abortSignal.aborted) {
          createEffectIfNeeded();

          const parts = await createContentPromise();
          if (parts && parts.length) {
            log.info(tag, "Yielding parts", {
              frozenCount: frozenParts.length,
              hasTail: parts.length > frozenParts.length,
            });

            yield { content: parts };
          }

          if (errorOccurred) break;
        }

        if (effectCleanup) {
          effectCleanup();
        }
      } catch (error) {
        log.error(tag, "Adapter error", error);
        throw error;
      }
    },
  };
}
